(() => {
  'use strict';
  const previous = window.VPHomeParachute;
  if (previous) previous.destroy();

  const parcelSelector = '.headline-sub .hero-side-icon.balloon';
  const layoutSelector = '#library-header,#header-placeholder,.site-header,#nav-placeholder,.hero-card,.headline-sub';
  const properties = ['--parcel-travel-x', '--parcel-travel-y', '--parcel-progress', '--parcel-float', 'visibility'];
  const classes = ['is-parachute-ready', 'is-parachute-landed', 'is-parachute-parked'];
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  const listeners = [];
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  let parcel = null;
  let subtitle = null;
  let header = null;
  let nav = null;
  let original = null;
  let geometry = null;
  let frame = 0;
  let dirty = true;
  let destroyed = false;
  let suspended = false;
  let resizeObserver = null;
  let motionObserver = null;
  let treeObserver = null;
  let observed = [];
  // Phone (<=720px, Josh 10/11): the parcel floats straight down on its own once the header shows. Tied to
  // scroll it fell at the page's own speed, so on a phone it looked frozen. Desktop stays scroll-driven.
  const PHONE_MAX = 720;
  const DROP_DELAY_MS = 700;
  const DROP_MS = 2600;
  // Desktop hold while the hero sun blocks the landing spot: the parcel hangs at this much of its fall (well above the
  // hole), then eases down at EASE of the remaining gap per frame once the sun has cleared.
  const HOLD_PROGRESS = .55, EASE = .09;
  let eased = null;
  let dropAt = 0;
  let dropDone = false;
  let dropFired = false;
  let bootAt = 0;

  function on(target, event, listener, options) {
    if (!target?.addEventListener) return;
    target.addEventListener(event, listener, options);
    listeners.push(() => target.removeEventListener(event, listener, options));
  }

  function setProperty(name, value) {
    if (parcel.style.getPropertyValue(name) !== value) parcel.style.setProperty(name, value);
  }

  function releaseParcel() {
    if (!parcel || !original) return;
    properties.forEach((name, index) => {
      const [value, priority] = original.properties[index];
      if (value) parcel.style.setProperty(name, value, priority);
      else parcel.style.removeProperty(name);
    });
    classes.forEach((name, index) => parcel.classList.toggle(name, original.classes[index]));
    original = null;
  }

  function connect() {
    const next = document.querySelector(parcelSelector);
    if (next !== parcel) {
      releaseParcel();
      parcel = next;
      if (parcel) {
        original = {
          properties: properties.map(name => [parcel.style.getPropertyValue(name), parcel.style.getPropertyPriority(name)]),
          classes: classes.map(name => parcel.classList.contains(name))
        };
      }
    }
    subtitle = parcel?.closest('.headline-sub') || null;
    const nextHeader = document.getElementById('library-header');
    if (nextHeader !== header) {
      motionObserver?.disconnect();
      header = nextHeader;
      if (header) motionObserver?.observe(header, { attributes: true, attributeFilter: ['data-motion'] });
    }
    nav = document.querySelector('.site-header') || document.getElementById('nav-placeholder');
    const targets = [document.documentElement, header, nav, subtitle, parcel, parcel?.closest('.hero-card')].filter(Boolean);
    if (targets.length !== observed.length || targets.some((target, index) => target !== observed[index])) {
      resizeObserver?.disconnect();
      targets.forEach(target => resizeObserver?.observe(target));
      observed = targets;
    }
  }

  // The headline's text, one rect per line of glyphs (not the full-width line boxes).
  function titleRuns() {
    const title = parcel?.closest('.hero-card')?.querySelector('.headline-title');
    if (!title || !document.createTreeWalker) return [];
    const runs = [];
    const walker = document.createTreeWalker(title, 4);
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      if (!node.textContent.trim()) continue;
      const range = document.createRange();
      range.selectNodeContents(node);
      for (const rect of range.getClientRects()) if (rect.width && rect.height) runs.push(rect);
    }
    return runs;
  }

  function scrollTop() {
    return Math.max(0, window.scrollY ?? window.pageYOffset ?? document.documentElement.scrollTop ?? 0);
  }

  function isPhone() {
    return (document.documentElement.clientWidth || window.innerWidth) <= PHONE_MAX;
  }

  // 0 until the drop starts, then eases in (canopy opening) and out (soft landing); 1 once landed, for good.
  function dropProgress(now) {
    if (dropDone) return 1;
    if (!dropAt) dropAt = now + DROP_DELAY_MS;
    const t = (now - dropAt) / DROP_MS;
    if (t <= 0) return 0;
    if (!dropFired) {
      dropFired = true;
      document.documentElement.dataset.vpParachuteDrop = 'started';
      window.dispatchEvent(new Event('vp-parachute-drop'));
    }
    if (t >= 1) { dropDone = true; return 1; }
    return t * t * (3 - 2 * t);
  }

  // Phone: stay hidden until it's known whether the parcel drops, so it never flashes at its landing spot
  // and then jumps up to start the fall. Never longer than 2 s after boot.
  function waiting(now) {
    if (!isPhone() || dropAt || dropDone || now - bootAt > 2000) return false;
    if (!header || !header.dataset.motion) return true;
    return motionAllowed() && !geometry;
  }

  function motionAllowed() {
    return !!header && header.dataset.motion === 'on' && !reduced?.matches;
  }

  function measure() {
    connect();
    geometry = null;
    if (!parcel || !subtitle) return;
    // CSS must use only these translations, without a transform transition or animation.
    setProperty('--parcel-travel-x', '0px');
    setProperty('--parcel-travel-y', '0px');
    const landing = parcel.getBoundingClientRect();
    const target = subtitle.getBoundingClientRect();
    const sky = header?.getBoundingClientRect();
    if (!sky?.width || !sky.height || !landing.width || !landing.height || !target.height) return;

    const scroll = scrollTop();
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const navHeight = nav?.getBoundingClientRect().height || 58;
    const maxScroll = Math.max(0, (document.scrollingElement || document.documentElement).scrollHeight - viewportHeight);
    // Phone: start straight above the landing spot so the parcel drops straight down (Josh 10/11);
    // desktop keeps its drift in from the right of the band.
    // Desktop: 72% across the sticker picture (home-collage.js frame), not the band, so on ultra-wide screens the
    // fall stays in the same gap between stickers.
    const frame = window.VPHomeFrame?.(sky);
    let sourceX = viewportWidth <= 720 ? landing.left + landing.width / 2
      : frame ? sky.left + sky.width / 2 + (.72 - .5) * frame.f.w * frame.sx : sky.left + sky.width * .72;
    // Phone (Josh 10/12, "the balloon is going over the text"): a headline line that reaches into the
    // landing column pushes the drop lane just right of its last letter; the parcel slides over into
    // its landing spot only once it is below that line (horizontalStart).
    let clearBottom = -Infinity;
    if (viewportWidth <= 720) {
      let clearRight = -Infinity;
      titleRuns().forEach(run => {
        if (run.right > landing.left - 6 && run.bottom > sky.top + sky.height * .5 && run.top < landing.bottom) {
          clearRight = Math.max(clearRight, run.right);
          clearBottom = Math.max(clearBottom, run.bottom + scroll);
        }
      });
      if (clearRight > -Infinity) sourceX = Math.max(sourceX, clearRight + 6 + landing.width / 2);
    }
    const sourceY = sky.top + scroll + sky.height * (viewportWidth <= 720 ? .82 : .62);
    const minX = Math.max(sky.left + 8, 8);
    const maxX = Math.min(sky.right - landing.width - 8, viewportWidth - landing.width - 8);
    const minY = sky.top + scroll + 8;
    const maxY = sky.bottom + scroll - landing.height - 8;
    if (maxY < minY || maxX < minX) return;
    const startX = clamp(sourceX - landing.width / 2, minX, maxX);
    const startY = clamp(sourceY - landing.height / 2, minY, maxY);
    const landingY = landing.top + scroll;
    // Spread the descent over more scrolling while keeping the canopy below navigation.
    const end = Math.min((landingY - startY) * .88, landingY - navHeight - 12, maxScroll);
    if (end <= 0 || startY >= landingY) return;
    let horizontalEnd = 1;
    let horizontalStart = 0;
    if (viewportWidth <= 720 && clearBottom > -Infinity && startX - landing.left > .5) {
      // Fraction of the fall at which the parcel's top has passed below the line it had to clear.
      horizontalStart = clamp(1 - (clearBottom + 2 - landingY) / (startY - landingY), 0, .97);
    } else if (viewportWidth <= 720) {
      const headline = parcel.closest('.hero-card')?.querySelector('.headline-title')?.getBoundingClientRect();
      // Reach the right-side landing column before the canopy crosses the headline.
      if (headline?.height) horizontalEnd = clamp((headline.top + scroll - landing.height - 12 - startY) / (landingY - startY), .05, 1);
    }
    geometry = { x: startX - landing.left, y: startY - landingY, end, horizontalEnd, horizontalStart };
  }

  function draw(now) {
    if (!parcel) return;
    const parked = !motionAllowed() || !geometry;
    const dropping = !parked && isPhone();
    let progress = parked ? 1 : dropping ? dropProgress(now) : clamp(scrollTop() / geometry.end, 0, 1);
    // Desktop (Josh 10/19): while the hero sun still sits in the landing spot (home-collage.js sets data-vp-sun-hold
    // on <html> until the sun has rolled clear), the parcel waits above the hole; afterwards it eases down to where the
    // scroll says it should be, instead of jumping. Pages that never set the hold behave exactly as before.
    if (!parked && !dropping) {
      const held = document.documentElement?.dataset?.vpSunHold === '1';
      const waiting = held && progress > HOLD_PROGRESS;
      if (held) progress = Math.min(progress, HOLD_PROGRESS);
      if (waiting || eased !== null) {
        if (eased === null) eased = progress;
        const gap = progress - eased;
        if (Math.abs(gap) < .0015) eased = waiting ? progress : null;
        else { eased += gap * EASE; progress = eased; }
      }
      // Keep drawing while the parcel waits on the sun (so it moves on the moment the hold lifts) or is easing down.
      if (waiting || eased !== null) schedule();
    }
    const remaining = 1 - progress;
    const horizontalProgress = parked ? 1 : geometry.horizontalStart
      ? clamp((progress - geometry.horizontalStart) / (1 - geometry.horizontalStart), 0, 1)
      : clamp(progress / geometry.horizontalEnd, 0, 1);
    const horizontalRemaining = 1 - horizontalProgress * horizontalProgress * (3 - 2 * horizontalProgress);
    setProperty('--parcel-travel-x', `${parked ? 0 : Number((geometry.x * horizontalRemaining).toFixed(3))}px`);
    setProperty('--parcel-travel-y', `${parked ? 0 : Number((geometry.y * remaining).toFixed(3))}px`);
    setProperty('--parcel-progress', String(Number(progress.toFixed(5))));
    setProperty('--parcel-float', String(parked ? 0 : Number(clamp(remaining * 4, 0, 1).toFixed(5))));
    parcel.classList.add('is-parachute-ready');
    parcel.classList.toggle('is-parachute-landed', progress === 1);
    parcel.classList.toggle('is-parachute-parked', parked);
    const hidden = waiting(now);
    setProperty('visibility', hidden ? 'hidden' : '');
    if (hidden || (dropping && progress < 1)) schedule();
  }

  // The home collage keeps its stickers out of the fall: publish where the parcel starts,
  // as an offset from its landing spot, and say when that changes.
  function publishPath() {
    if (!parcel) return;
    const path = geometry ? `${Math.round(geometry.x)},${Math.round(geometry.y)}` : '';
    if (parcel.dataset.fall === path) return;
    parcel.dataset.fall = path;
    window.dispatchEvent(new Event('vp-parachute-path'));
  }

  function render(time) {
    frame = 0;
    if (destroyed || suspended || document.hidden) return;
    if (dirty) {
      dirty = false;
      measure();
      publishPath();
    }
    draw(typeof time === 'number' ? time : window.performance?.now?.() ?? Date.now());
  }

  function schedule() {
    if (destroyed || suspended || document.hidden || frame) return;
    frame = window.requestAnimationFrame(render);
  }

  function refresh() {
    dirty = true;
    schedule();
  }

  function stop() {
    if (frame) window.cancelAnimationFrame(frame);
    frame = 0;
  }

  function relevantNode(node) {
    return node.nodeType === 1 && (node.matches(layoutSelector + ',' + parcelSelector) || node.querySelector(layoutSelector + ',' + parcelSelector));
  }

  function start() {
    if (destroyed || !document.body?.classList.contains('home-page')) return;
    bootAt = window.performance?.now?.() ?? Date.now();
    if (window.ResizeObserver) resizeObserver = new window.ResizeObserver(refresh);
    if (window.MutationObserver) {
      motionObserver = new window.MutationObserver(refresh);
      treeObserver = new window.MutationObserver(records => {
        const changed = records.some(record => subtitle?.contains(record.target) ||
          [...(record.addedNodes || []), ...(record.removedNodes || [])].some(relevantNode));
        if (changed) refresh();
      });
      // VP.inject replaces the placeholder itself; observing just that node misses the header.
      treeObserver.observe(document.body, { childList: true, subtree: true, characterData: true });
    }
    on(window, 'scroll', () => { if (motionAllowed()) schedule(); }, { passive: true });
    on(window, 'resize', refresh, { passive: true });
    on(window, 'orientationchange', refresh, { passive: true });
    on(window.visualViewport, 'resize', refresh, { passive: true });
    on(window, 'load', refresh);
    on(window, 'pageshow', () => { suspended = false; refresh(); });
    on(window, 'pagehide', event => {
      if (!event.persisted) { destroy(); return; }
      suspended = true;
      stop();
    });
    on(window, 'popstate', refresh);
    on(window, 'hashchange', refresh);
    on(document, 'visibilitychange', () => {
      if (document.hidden) stop();
      else refresh();
    });
    on(document, 'load', event => {
      if (event.target === parcel || header?.contains(event.target) || nav?.contains(event.target)) refresh();
    }, true);
    on(document.fonts, 'loadingdone', refresh);
    on(document.fonts, 'loadingerror', refresh);
    document.fonts?.ready?.then(refresh, refresh);
    if (reduced?.addEventListener) on(reduced, 'change', refresh);
    else if (reduced?.addListener) {
      reduced.addListener(refresh);
      listeners.push(() => reduced.removeListener(refresh));
    }
    render();
  }

  function destroy() {
    if (destroyed) return;
    destroyed = true;
    stop();
    listeners.forEach(remove => remove());
    resizeObserver?.disconnect();
    motionObserver?.disconnect();
    treeObserver?.disconnect();
    releaseParcel();
    observed = [];
    if (window.VPHomeParachute === controller) delete window.VPHomeParachute;
  }

  const controller = { refresh, destroy };
  window.VPHomeParachute = controller;
  if (document.readyState === 'loading') on(document, 'DOMContentLoaded', start, { once: true });
  else start();
})();
